import {useState, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {Modal, Blockquote, Loading, FormGroup, Select, SelectItem, TextArea} from "@wfp/ui";
import {useRetrieveDiscardReasons} from "../../hooks/discard-reason";

interface Props {
    open: boolean;
    onClose: () => void;
    onConfirm: (reasonId: number, notes: string) => void;
    title: string;
    entityName: string;
    loading?: boolean;
    error?: string | null;
}

export const DiscardModal = (props: Props) => {
    const {t} = useTranslation();
    const [selectedReasonId, setSelectedReasonId] = useState<string>("");
    const [notes, setNotes] = useState<string>("");
    const [validationError, setValidationError] = useState<string | null>(null);

    const {data: discardReasons, isLoading: reasonsLoading} = useRetrieveDiscardReasons();

    // Reset form when modal opens/closes
    useEffect(() => {
        if (props.open) {
            setSelectedReasonId("");
            setNotes("");
            setValidationError(null);
        }
    }, [props.open]);

    const handleConfirm = () => {
        // Validate reason selection
        if (!selectedReasonId) {
            setValidationError(t('please_select_discard_reason'));
            return;
        }

        setValidationError(null);
        props.onConfirm(parseInt(selectedReasonId), notes);
    };

    const handleClose = () => {
        setSelectedReasonId("");
        setNotes("");
        setValidationError(null);
        props.onClose();
    };

    const isFormDisabled = props.loading || reasonsLoading;

    return (
        <Modal
            open={props.open}
            modalHeading={props.title}
            modalLabel={t('discard')}
            primaryButtonText={t('confirm') as string}
            onRequestSubmit={handleConfirm}
            onRequestClose={handleClose}
            secondaryButtonText={t('cancel') as string}
            onSecondarySubmit={handleClose}
            primaryButtonDisabled={isFormDisabled || !selectedReasonId}
            danger
        >
            <div className="mb-4">
                <p>{t('discard_confirmation_message', {entity: props.entityName})}</p>
            </div>

            {reasonsLoading ? (
                <div className="mt-4 flex items-center">
                    <Loading withOverlay={false} />
                    <span className="ml-3">{t('loading_reasons')}</span>
                </div>
            ) : (
                <>
                    <FormGroup legendText="">
                        <Select
                            id="discard-reason-select"
                            labelText={t('discard_reason') as string}
                            value={selectedReasonId}
                            onChange={(e: any) => {
                                setSelectedReasonId(e.target.value);
                                setValidationError(null);
                            }}
                            disabled={isFormDisabled}
                            invalid={!!validationError}
                            invalidText={validationError || undefined}
                        >
                            <SelectItem value="" text={t('select_reason') as string} />
                            {discardReasons?.filter(r => !r.disabled).map(reason => (
                                <SelectItem
                                    key={reason.id}
                                    value={reason.id.toString()}
                                    text={reason.name}
                                />
                            ))}
                        </Select>
                    </FormGroup>

                    <FormGroup legendText="">
                        <TextArea
                            id="discard-notes"
                            labelText={t('notes') as string}
                            placeholder={t('optional_notes_placeholder') as string}
                            value={notes}
                            onChange={(e: any) => setNotes(e.target.value)}
                            disabled={isFormDisabled}
                            maxLength={500}
                            rows={4}
                        />
                        <div className="text-xs text-gray-500 mt-1">
                            {notes.length}/500 {t('characters')}
                        </div>
                    </FormGroup>
                </>
            )}

            {props.loading && (
                <div className="mt-4 flex items-center">
                    <Loading withOverlay={false} />
                    <span className="ml-3">{t('processing')}</span>
                </div>
            )}

            {props.error && (
                <Blockquote kind="error" withIcon className="mt-4">
                    {props.error}
                </Blockquote>
            )}
        </Modal>
    );
};
